import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Hint } from "~/features/chat/types";

interface TooltipState {
  term: string;
  definition: string;
  x: number;
  y: number;
  termHeight: number;
}

interface TooltipPosition {
  left: number;
  top: number;
}

interface TermTooltipProps {
  /** The container element to attach event delegation to */
  containerRef: HTMLElement | null;
  /** Map of lowercased term → definition for O(1) lookup */
  termMap: Map<string, string>;
}

const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 8;

/**
 * A single shared tooltip that appears on hover over `.term-highlight` marks.
 * Uses event delegation — one mouseover/mouseout listener on the container —
 * instead of mounting a component per term.
 *
 * Viewport-aware: prefers placing above the term, flips below when clipped.
 * Horizontal position is clamped to stay within the viewport.
 */
export function TermTooltip({ containerRef, termMap }: TermTooltipProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [pos, setPos] = useState<TooltipPosition | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const showTooltip = useCallback(
    (el: HTMLElement) => {
      const key = el.getAttribute("data-term-key");
      if (!key) return;

      const definition = termMap.get(key);
      if (!definition) return;

      cancelHide();

      const rect = el.getBoundingClientRect();
      setPos(null);
      setTooltip({
        term: el.textContent ?? key,
        definition,
        x: rect.left + rect.width / 2,
        y: rect.top,
        termHeight: rect.height,
      });
    },
    [termMap, cancelHide]
  );

  const hideTooltip = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltip(null);
      setPos(null);
      hideTimeoutRef.current = null;
    }, 150);
  }, []);

  useEffect(() => {
    if (!containerRef) return;

    const handleMouseOver = (e: MouseEvent) => {
      const mark = (e.target as Element).closest<HTMLElement>(
        "mark.term-highlight"
      );
      if (mark && containerRef.contains(mark)) {
        showTooltip(mark);
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const mark = (e.target as Element).closest<HTMLElement>(
        "mark.term-highlight"
      );
      if (mark) {
        hideTooltip();
      }
    };

    containerRef.addEventListener("mouseover", handleMouseOver);
    containerRef.addEventListener("mouseout", handleMouseOut);

    return () => {
      containerRef.removeEventListener("mouseover", handleMouseOver);
      containerRef.removeEventListener("mouseout", handleMouseOut);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [containerRef, showTooltip, hideTooltip]);

  // Measure the tooltip and compute viewport-aware position before paint
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!tooltip || !el) return;

    const ttHeight = el.offsetHeight;
    const ttWidth = el.offsetWidth;
    const vh = window.innerHeight;

    // Vertical: pick side with more available space, clamp to viewport
    const spaceAbove = tooltip.y - TOOLTIP_GAP;
    const spaceBelow = vh - (tooltip.y + tooltip.termHeight + TOOLTIP_GAP);
    const placeAbove = spaceAbove >= ttHeight || spaceAbove >= spaceBelow;
    let top = placeAbove
      ? tooltip.y - TOOLTIP_GAP - ttHeight
      : tooltip.y + tooltip.termHeight + TOOLTIP_GAP;
    top = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, vh - ttHeight - VIEWPORT_PADDING)
    );

    // Horizontal: center on term, clamp to viewport edges
    let left = tooltip.x - ttWidth / 2;
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - ttWidth - VIEWPORT_PADDING)
    );

    setPos({ left, top });
  }, [tooltip]);

  if (!tooltip) return null;

  return (
    <div
      ref={tooltipRef}
      className="fixed z-[9999] pointer-events-none"
      style={
        pos
          ? { left: `${pos.left}px`, top: `${pos.top}px` }
          : {
              left: "0px",
              top: "0px",
              visibility: "hidden",
            }
      }
    >
      <div
        className="bg-popover text-popover-foreground border border-border rounded-md shadow-md px-3 py-2 max-w-[300px] max-h-[200px] overflow-y-auto pointer-events-auto"
        onMouseEnter={cancelHide}
        onMouseLeave={hideTooltip}
      >
        <h4 className="font-semibold text-xs mb-0.5">{tooltip.term}</h4>
        <p className="text-xs text-muted-foreground leading-snug m-0">
          {tooltip.definition}
        </p>
      </div>
    </div>
  );
}

/**
 * Builds an O(1) lookup map from term arrays.
 * Medications are added first, then AI terms override (higher priority).
 * Memoize this with useMemo in the consuming component.
 */
export function buildTermMap(
  terms: Hint[],
  medications?: Hint[]
): Map<string, string> {
  const map = new Map<string, string>();
  if (medications) {
    for (const m of medications) {
      map.set(m.term.toLowerCase(), m.definition);
    }
  }
  for (const t of terms) {
    map.set(t.term.toLowerCase(), t.definition);
  }
  return map;
}
